import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Select,
  Table,
  Switch,
  Modal,
  Tag,
  Empty,
  Typography,
  Drawer,
  message,
} from "antd";
import { PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { debounce } from "lodash";
import PageHeader from "../Layout/PageHeader";
import { formatDateTime } from "../../helpers/date";

const validValues = (form) => form.validateFields().catch(() => null);

const showError = (error) =>
  message.error(error.response?.data?.message || error.message);

const RecipientsSelect = (props) => (
  <Select
    {...props}
    mode="tags"
    tokenSeparators={[",", " "]}
    open={false}
    placeholder="name@parceldaily.com"
  />
);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const recipientsRules = [
  { required: true, message: "Add at least one recipient" },
  {
    validator: (_, recipients = []) => {
      const invalid = recipients.find((email) => !EMAIL_PATTERN.test(email));
      return invalid
        ? Promise.reject(new Error(`${invalid} is not a valid email`))
        : Promise.resolve();
    },
  },
];

const RecipientsField = () => (
  <Form.Item
    name="recipients"
    label="Send emails to"
    rules={recipientsRules}
    extra="Press Enter or a comma after each email. You can also paste a list."
  >
    <RecipientsSelect />
  </Form.Item>
);

const AccountSearch = (props) => {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const latestSearch = useRef(0);

  const search = useMemo(
    () =>
      debounce(async (text) => {
        const searchId = ++latestSearch.current;
        if (text.trim().length < 3) {
          setOptions([]);
          setSearching(false);
          return;
        }
        setSearching(true);
        try {
          const res = await axios.get("/api/order-watches/accounts", {
            params: { search: text },
          });
          if (searchId !== latestSearch.current) return;
          setOptions(
            res.data.data.map((account) => ({
              value: account.email,
              label: account.name
                ? `${account.name} (${account.email})`
                : account.email,
            })),
          );
        } catch (error) {
          if (searchId === latestSearch.current) showError(error);
        } finally {
          if (searchId === latestSearch.current) setSearching(false);
        }
      }, 300),
    [],
  );

  useEffect(
    () => () => {
      search.cancel();
      latestSearch.current += 1;
    },
    [search],
  );

  return (
    <Select
      {...props}
      showSearch
      filterOption={false}
      onSearch={search}
      options={options}
      loading={searching}
      notFoundContent={searching ? null : "Type at least 3 letters"}
      placeholder="Search by email or company name"
    />
  );
};

const AccountCell = ({ record }) => (
  <>
    <div>{record.accountName || record.userEmail}</div>
    {record.accountName && (
      <Typography.Text type="secondary">{record.userEmail}</Typography.Text>
    )}
  </>
);

const AboutOrderWatch = ({ visible, onClose }) => (
  <Drawer
    title="Order Watch"
    placement="right"
    onClose={onClose}
    visible={visible}
    width={480}
  >
    <h3>What is Order Watch?</h3>
    <p>
      It emails the details of every new paid order from a chosen account to the
      people you pick.
    </p>
    <ul>
      <li>
        Each email is sent once the order has its AWB, usually within a few
        minutes.
      </li>
      <li>Only orders paid after the account was added are sent.</li>
      <li>Each order is emailed once.</li>
      <li>
        Pausing stops the emails. When you resume, orders paid while paused are
        sent too.
      </li>
    </ul>
  </Drawer>
);

const OrderWatch = () => {
  const [watches, setWatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchWatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/order-watches");
      setWatches(res.data.data);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatches();
  }, [fetchWatches]);

  const save = async (request, successMessage) => {
    setSaving(true);
    try {
      await request();
      message.success(successMessage);
      fetchWatches();
      return true;
    } catch (error) {
      showError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    addForm.resetFields();
    setIsAdding(true);
  };

  const addWatch = async () => {
    const values = await validValues(addForm);
    if (!values) return;
    if (
      await save(
        () => axios.post("/api/order-watches", values),
        "Account added",
      )
    )
      setIsAdding(false);
  };

  const updateWatch = (id, values, successMessage) =>
    save(() => axios.patch(`/api/order-watches/${id}`, values), successMessage);

  const openEdit = (record) => {
    setEditing(record);
    editForm.setFieldsValue({ recipients: record.recipients });
  };

  const saveRecipients = async () => {
    const values = await validValues(editForm);
    if (!values) return;
    if (await updateWatch(editing.objectId, values, "Recipients updated"))
      setEditing(null);
  };

  const accountLabel = (record) => record.accountName || record.userEmail;

  const toggleActive = (record, isActive) => {
    if (isActive) {
      updateWatch(record.objectId, { isActive }, "Emails resumed");
      return;
    }
    Modal.confirm({
      title: `Pause emails for ${accountLabel(record)}?`,
      okText: "Pause",
      onOk: () => updateWatch(record.objectId, { isActive }, "Emails paused"),
    });
  };

  const removeWatch = (record) =>
    Modal.confirm({
      title: `Stop monitoring ${accountLabel(record)}?`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: () =>
        save(
          () => axios.delete(`/api/order-watches/${record.objectId}`),
          "Account removed",
        ),
    });

  const columns = [
    {
      title: "Account",
      key: "account",
      render: (_, record) => <AccountCell record={record} />,
    },
    {
      title: "Recipients",
      dataIndex: "recipients",
      key: "recipients",
      render: (recipients) =>
        recipients.map((recipient) => <Tag key={recipient}>{recipient}</Tag>),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => toggleActive(record, checked)}
        />
      ),
    },
    {
      title: "Monitoring since",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => formatDateTime(createdAt),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button type="link" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Button type="link" danger onClick={() => removeWatch(record)}>
            Remove
          </Button>
        </>
      ),
    },
  ];

  const emptyState = (
    <Empty description="No accounts monitored yet">
      <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
        Add account
      </Button>
    </Empty>
  );

  return (
    <div>
      <PageHeader
        title="Order Watch"
        childRoutes={[{ path: "/order/watch", breadcrumbName: "Order Watch" }]}
        additionalInfo={() => (
          <InfoCircleOutlined onClick={() => setIsAboutOpen(true)} />
        )}
      />
      <AboutOrderWatch
        visible={isAboutOpen}
        onClose={() => setIsAboutOpen(false)}
      />

      <Row justify="end" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add account
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={watches}
        loading={loading}
        pagination={false}
        locale={{ emptyText: emptyState }}
      />

      <Modal
        title="Add account"
        visible={isAdding}
        okText="Add"
        onOk={addWatch}
        onCancel={() => setIsAdding(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" preserve={false}>
          <Form.Item
            name="userEmail"
            label="Account"
            rules={[{ required: true, message: "Choose an account" }]}
          >
            <AccountSearch />
          </Form.Item>
          <RecipientsField />
        </Form>
      </Modal>

      <Modal
        title={editing && `Recipients for ${accountLabel(editing)}`}
        visible={!!editing}
        onCancel={() => setEditing(null)}
        onOk={saveRecipients}
        confirmLoading={saving}
      >
        <Form form={editForm} layout="vertical">
          <RecipientsField />
        </Form>
      </Modal>
    </div>
  );
};

export default OrderWatch;
